import { Box } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { EnvFeatureFlagContext } from "../../../../contexts/EnvFeaturesContext";
import { MUI_SPACING } from "../../../../utils/constants";
import { CARD_WIDTH as entityTypeCardWidth } from "../components/EntityTypeOverviewCard/EntityTypeOverviewCard";
import { overviewType } from "../EnvironmentView";
import useFilterEntities from "../filters/useFilterEntities";
import {
  getInitialFilters,
  getInitialLogics,
  hasAnyFiltersApplied,
} from "../filters/utils";
import EntitiesOverviewCardView, {
  CARDS_GAP,
} from "./EntitiesOverviewCardView";
import EntityTopOverview from "./EntityTopOverview.jsx/EntityTopOverview";
import EntitiesOverviewTableView from "./TableView/EntitiesOverviewTableView";
import RdsEntitiesOverviewTableView from "./TableView/RdsEntitiesOverviewTableView";

export default function EntityOverviewView({
  useQueryForAllEntities,
  entitiesOverviewData,
  assets,
}) {
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const [selectedOverviewType, setSelectedOverviewType] = useState(
    overviewType.CARD
  );
  const containerRef = useRef(null);
  const [cardsContainerWidth, setCardsContainerWidth] = useState(1300);
  const [postgresFilteredEntities, setPostgresFilteredEntities] =
    useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState(() =>
    getInitialFilters()
  );
  const [filterAndOrLogics, setFilterAndOrLogics] = useState(() =>
    getInitialLogics()
  );

  const [visibleColumns, setVisibleColumns] = useState(null);
  const [rdsSearchPayload, setRdsSearchPayload] = useState(null);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const numOfCardsInLine =
          ((containerRef.current.offsetWidth + CARDS_GAP * MUI_SPACING) /
            (entityTypeCardWidth + CARDS_GAP * MUI_SPACING)) |
          0;
        const newWidth =
          numOfCardsInLine * entityTypeCardWidth +
          (numOfCardsInLine - 1) * CARDS_GAP * MUI_SPACING;
        setCardsContainerWidth(newWidth);
      }
    };

    const observer = new ResizeObserver(updateWidth);
    if (containerRef.current) observer.observe(containerRef.current);
    window.addEventListener("resize", updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  const entitiesToFilter =
    postgresFilteredEntities !== null
      ? postgresFilteredEntities
      : useQueryForAllEntities.data || [];

  const filteredEntities = useFilterEntities(
    isActiveRds ? [] : entitiesToFilter,
    appliedFilters,
    filterAndOrLogics,
    assets
  );

  const topOverviewWith = cardsContainerWidth;

  const anyFilterApplied = hasAnyFiltersApplied(appliedFilters);

  if (anyFilterApplied && selectedOverviewType !== overviewType.TABLE)
    setSelectedOverviewType(overviewType.TABLE);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <EntityTopOverview
        overviewData={entitiesOverviewData.total_overview}
        useQueryForAllEntities={useQueryForAllEntities}
        assets={assets}
        width={topOverviewWith}
        selectedOverviewType={selectedOverviewType}
        setSelectedOverviewType={setSelectedOverviewType}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        filterAndOrLogics={filterAndOrLogics}
        setFilterAndOrLogics={setFilterAndOrLogics}
        setPostgresFilteredEntities={setPostgresFilteredEntities}
        setIsSearching={setIsSearching}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        onRdsSearchApply={
          isActiveRds
            ? (filters, filterLogic) => {
                const normalizedFilters = (filters || []).map((f) => {
                  let processedValue = f.value;
                  if (f.value === "true") processedValue = true;
                  if (f.value === "false") processedValue = false;
                  return {
                    field: f.field,
                    op: f.op,
                    value: processedValue,
                    target_table: f.target_table,
                  };
                });
                setRdsSearchPayload({
                  filters: normalizedFilters,
                  filter_logic: filterLogic || [],
                });
              }
            : undefined
        }
        onRdsSearchClear={
          isActiveRds
            ? () => {
                setRdsSearchPayload(null);
              }
            : undefined
        }
      />
      <>
        {selectedOverviewType === overviewType.CARD ? (
          <EntitiesOverviewCardView
            entitiesOverviewData={entitiesOverviewData}
            entitiesAreLoading={
              isActiveRds ? false : useQueryForAllEntities.isLoading
            }
            containerRef={containerRef}
          />
        ) : selectedOverviewType === overviewType.TABLE ? (
          isActiveRds ? (
            <RdsEntitiesOverviewTableView
              assets={assets}
              visibleColumns={visibleColumns}
              setVisibleColumns={setVisibleColumns}
              filtersPayload={rdsSearchPayload}
            />
          ) : (
            <EntitiesOverviewTableView
              filteredEntities={filteredEntities}
              assets={assets}
              isSearching={isSearching}
              visibleColumns={visibleColumns}
              setVisibleColumns={setVisibleColumns}
              useQueryForAllEntities={useQueryForAllEntities}
            />
          )
        ) : null}
      </>
    </Box>
  );
}
